// Product images (DLG-021, parity pages/catalog.md §2 "Image"; plans/2026-10-12-product-images): the item editor's
// Upload and Remove. Legacy saved the file on the API server's disk (`POST /v1/upload/{id}`) and online ordering showed
// it; the register shows no product images. Here the bytes travel as base64 in a cloud command (like the brand images),
// are kept in the cloud's object store under their sha256, and a query returns them, sending the bytes only when the
// reader does not already hold that hash.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { IsoInstant, LocationId, ProductId } from '../../primitives/codecs.ts';
import { CatalogEntityNotFound } from './errors.ts';

/** Legacy `ImageUploadRequest`: `mimes:jpeg,png,jpg,gif`. */
export const ProductImageType = z.enum(['image/jpeg', 'image/png', 'image/gif']).meta({ id: 'ProductImageType' });

/** 10 MB (core `PRODUCT_IMAGE_MAX_BYTES`, the limit the editor states) as base64: four characters per three bytes. */
const PRODUCT_IMAGE_MAX_BASE64 = Math.ceil((10 * 1024 * 1024) / 3) * 4;

/** The request body cap for a product image upload: the image's base64 plus the envelope. */
export const PRODUCT_IMAGE_BODY_BYTES = PRODUCT_IMAGE_MAX_BASE64 + 64 * 1024;

export const ProductImageData = z.string().min(4).max(PRODUCT_IMAGE_MAX_BASE64).regex(/^[A-Za-z0-9+/]*={0,2}$/).meta({ id: 'ProductImageData' });

export const ProductImageInvalid = defineError({
  code: 'catalog.product_image_invalid', httpStatus: 422, message: 'errors.catalog.product_image_invalid', retryable: false,
  /** `not_staged`: a staged image names a sha256 this account never staged (onboarding.menu.stageImage). */
  details: z.object({ problem: z.enum(['too_large', 'not_an_image', 'not_staged']) }),
});

/** The object store is not configured on this server (an operator error, not the merchant's). */
export const ProductImageStoreUnavailable = defineError({
  code: 'catalog.product_image_store_unavailable', httpStatus: 503, message: 'errors.catalog.product_image_store_unavailable', retryable: true,
  details: z.object({}),
});

export const ProductImageMeta = z
  .object({ contentType: ProductImageType, sha256: z.string(), byteSize: z.int().positive(), setAt: IsoInstant })
  .meta({ id: 'ProductImageMeta' });

export const SetProductImage = defineCommand({
  name: 'catalog.setProductImage',
  summary: "Set or remove a product's image (the item editor's Upload and Remove).",
  input: z.object({
    productId: ProductId,
    locationId: LocationId,
    /**
     * The image's bytes, or (the menu build's apply) a sha256 this account already staged with
     * onboarding.menu.stageImage, so a batch never carries bytes. null removes the image.
     */
    image: z.union([
      z.object({ contentType: ProductImageType, data: ProductImageData }),
      z.object({ staged: z.object({ sha256: z.string().regex(/^[0-9a-f]{64}$/) }) }),
    ]).nullable(),
  }),
  output: z.object({ productId: ProductId, image: ProductImageMeta.nullable() }),
  permission: 'items.manage',
  risk: 'medium',
  availability: 'cloud',
  cloudReason: 'Product images are kept in the cloud, as legacy kept them on its API server; online ordering and the back office read them there.',
  origin: 'any',
  emits: [],
  errors: [CatalogEntityNotFound, ProductImageInvalid, ProductImageStoreUnavailable],
  dryRun: true,
  maxBodyBytes: PRODUCT_IMAGE_BODY_BYTES,
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['POST /v1/upload/{id}', 'PUT /v1/products/{id} (remove_image)'],
});

export const ProductImage = defineQuery({
  name: 'catalog.productImage',
  summary: "A product's image, with its bytes unless the caller already holds that hash.",
  input: z.object({ productId: ProductId, locationId: LocationId, held: z.string().optional() }),
  output: z.object({ image: ProductImageMeta.extend({ data: z.string().nullable() }).nullable() }),
  permission: 'items.manage',
  availability: 'cloud',
  cloudReason: 'Product images are kept in the cloud.',
  scope: 'location',
  errors: [CatalogEntityNotFound, ProductImageStoreUnavailable],
  agent: { expose: false },
  legacy: ['GET /v1/products/{id} (image)'],
});

export const productImageCommands = [SetProductImage] as const;
export const productImageQueries = [ProductImage] as const;
